<template>
  <div v-if="isLoaded">
    <!-- 絶対優先の場合 -->
    <div v-if="cloudQosDetail.qosType === 'ABSOLUTE'">
      <v-table density="compact" class="table-bordered">
        <thead class="text-center">
          <tr>
            <td class="table-light-color">QoS方式</td>
            <td colspan="2">
              {{ $filter.enumTo(cloudQosDetail.qosType, "qosType") }}
            </td>
            <td class="table-light-color" colspan="3">優先度参照先</td>
            <td colspan="4">
              {{
                $filter.enumTo(
                  cloudQosDetail.priorityTarget,
                  "cloudQos_PriorityTarget"
                )
              }}
            </td>
          </tr>
          <tr>
            <th class="table-light-color" colspan="2"></th>
            <th colspan="8" class="text-center table-light-color">
              {{
                $filter.enumTo(
                  cloudQosDetail.priorityTarget,
                  "cloudQos_PriorityTarget"
                )
              }}
            </th>
          </tr>
          <tr class="table-light-color">
            <th></th>
            <th>IP Precedence</th>
            <th class="text-center" v-for="index in 8" :key="index">
              {{ index - 1 }}
            </th>
          </tr>
          <tr class="table-light-color">
            <th></th>
            <th>DSCP</th>
            <th
              class="text-center"
              v-for="(colName, colNameIndex) in absolutePriorityColNames"
              :key="colNameIndex"
            >
              {{ colName }}
            </th>
          </tr>
        </thead>
        <tbody class="text-center">
          <th :rowspan="5" class="table-light-color">優先度</th>
          <tr
            v-for="(rowName, rowNameIndex) in absolutePriorityRowNames"
            :key="rowNameIndex"
          >
            <th class="text-left table-light-color">{{ rowName.value }}</th>
            <td
              v-for="(propName, propNameIndex) in absolutePriorityPropNames"
              :key="propNameIndex"
            >
              {{
                cloudQosDetail.cloudQosAbsolute &&
                cloudQosDetail.cloudQosAbsolute[propName] === rowName.key
                  ? "●"
                  : "○"
              }}
            </td>
          </tr>
        </tbody>
      </v-table>
    </div>

    <!-- 未登録の場合 -->
    <template v-else-if="cloudQosDetail.qosType === 'NONE'">
      <app-label-group label="QoS方式" value="設定無し" />
    </template>

    <!-- 帯域優先の場合 -->
    <div v-else-if="cloudQosDetail.qosType === 'BANDWIDTH'">
      <v-table density="compact" class="table-bordered">
        <thead>
          <tr class="text-center">
            <td class="table-light-color">QoS方式</td>
            <td colspan="2">
              {{ $filter.enumTo(cloudQosDetail.qosType, "qosType") }}
            </td>
            <td class="table-light-color" colspan="2">優先度参照先</td>
            <td colspan="2">
              {{
                $filter.enumTo(
                  cloudQosDetail.priorityTarget,
                  "cloudQos_PriorityTarget"
                )
              }}
            </td>
          </tr>
          <tr class="table-light-color">
            <th colspan="2" rowspan="2"></th>
            <th colspan="5" class="text-center">
              {{
                $filter.enumTo(
                  cloudQosDetail.priorityTarget,
                  "cloudQos_PriorityTarget"
                )
              }}
            </th>
          </tr>
          <tr class="table-light-color">
            <th
              v-for="(colName, colNameIndex) in bandwidthColNames"
              :key="colNameIndex"
            >
              {{ colName }}
            </th>
          </tr>
        </thead>

        <tbody>
          <template
            v-for="(rowName, rowNameIndex) in bandwidthRowNames"
            :key="rowNameIndex"
          >
            <tr
              v-if="!bandControlPattern || rowName.key === bandControlPattern"
            >
              <template v-if="rowNameIndex % 2 === 0">
                <th rowspan="2" class="table-light-color">
                  {{ rowName.value }}
                </th>
                <th class="table-light-color">割当帯域</th>
                <td
                  v-for="(propName, propNameIndex) in bandwidthPropNames"
                  :key="propNameIndex"
                  class="text-right"
                >
                  {{ getBandwidthValue(rowName.key, propName) }}
                </td>
              </template>
              <template v-else>
                <th class="table-light-color">最優先</th>
                <td></td>
                <td></td>
                <td></td>
                <td class="text-right">
                  {{ isDscp46TopPriority(rowName.key) }}
                </td>
                <td></td>
              </template>
            </tr>
          </template>
        </tbody>
      </v-table>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType } from "vue";
import type { CloudQosDetail } from "@/apis/CloudQosApi";
import type { ControlPattern } from "@/apis/VNConnectApi";
import AppLabelGroup from "@/components/AppLabelGroup.vue";
import type { NonNullableProperty } from "@/shims-vue";

export default defineComponent({
  name: "CloudQosSetting",
  components: { AppLabelGroup },
  props: {
    /** VPN/VNコード */
    vpnVnCode: {
      type: String as PropType<string>,
      required: true,
    },
    /** QoS帯域制御パターン。指定した場合はそのパターンのみが表示される */
    bandControlPattern: {
      type: String as PropType<ControlPattern | null>,
      required: false,
    },
  },
  data() {
    return {
      isLoaded: false,
      absolutePriorityRowNames: [
        { key: "HIGH", value: "High" },
        { key: "MEDIUM", value: "Medium" },
        { key: "NORMAL", value: "Normal" },
        { key: "LOW", value: "Low" },
      ],
      absolutePriorityColNames: ["0", "10", "18", "24", "34", "46", "-", "-"],
      absolutePriorityPropNames: [
        "priority0",
        "priority1",
        "priority2",
        "priority3",
        "priority4",
        "priority5",
        "priority6",
        "priority7",
      ] as (keyof NonNullableProperty<
        Pick<CloudQosDetail, "cloudQosAbsolute">
      >["cloudQosAbsolute"])[],
      bandwidthColNames: ["18", "24", "34", "46", "ベストエフォート"],
      bandwidthRowNames: [
        { key: "PATTERN_A", value: "パターンA" },
        { key: "PATTERN_A", value: "" },
        { key: "PATTERN_B", value: "パターンB" },
        { key: "PATTERN_B", value: "" },
      ],
      bandwidthPropNames: [
        "dscp18Value",
        "dscp24Value",
        "dscp34Value",
        "dscp46Value",
        "bestEffort",
      ],
      cloudQosDetail: {} as CloudQosDetail,
    };
  },
  async mounted() {
    this.cloudQosDetail = await this.$api.cloudQosApi.getCloudQosDetail(
      this.vpnVnCode
    );
    this.isLoaded = true;
  },
  methods: {
    /**
     * 帯域制御方式の値を取得
     * @param patternName パターン名
     * @param propName プロパティ名
     * */
    getBandwidthValue: function (patternName: string, propName: string) {
      const item = this.cloudQosDetail.cloudQosBandwidthList?.find(
        (e) => e.patternName === patternName
      );
      if (item) {
        if (propName === "bestEffort") {
          return (item.dscp18Value || 0) +
            (item.dscp24Value || 0) +
            (item.dscp34Value || 0) +
            (item.dscp46Value || 0) >
            0
            ? `${
                100 -
                ((item.dscp18Value || 0) +
                  (item.dscp24Value || 0) +
                  (item.dscp34Value || 0) +
                  (item.dscp46Value || 0))
              }%`
            : "-";
        } else {
          switch (propName) {
            case "dscp18Value":
              return item.dscp18Value ? `${item.dscp18Value}%` : "-";
            case "dscp24Value":
              return item.dscp24Value ? `${item.dscp24Value}%` : "-";
            case "dscp34Value":
              return item.dscp34Value ? `${item.dscp34Value}%` : "-";
            case "dscp46Value":
              return item.dscp46Value ? `${item.dscp46Value}%` : "-";
          }
        }
      } else {
        return "-";
      }
    },
    /**
     * 帯域制御方式の最優先設定フラグを取得
     * @param patternName パターン名
     * */
    isDscp46TopPriority(patternName: string) {
      const item = this.cloudQosDetail.cloudQosBandwidthList?.find(
        (e) => e.patternName === patternName
      );
      if (item) {
        return item.isDscp46TopPriority ? "ON" : "OFF";
      } else {
        return "-";
      }
    },
  },
});
</script>
<style lang="scss" scoped>
th {
  font-weight: 700 !important;
  font-size: 16px;
}
td {
  font-size: 16px;
}
.table-light-color {
  background-color: #e9ecef;
}
</style>
